/* ==========================================================================
   Shipping Protection — cart opt-in behaviour
   Theme: Laundry Leaf v3.31 | Shipping Protection

   Partner files:
     snippets/shipping-protection.liquid  (markup + config)
     assets/shipping-protection.css       (styles)

   WHAT THIS DOES
   The toggle adds or removes the Shipping Protection product from the cart
   immediately, the moment it is flipped. The line item is then hidden from
   every cart surface in Liquid, so the customer sees a toggle rather than a
   $1.99 product sitting among their real purchases. Checkout is left
   completely alone — no interception, no delay on the Checkout click.

   The cart is the source of truth. `localStorage` only records the customer's
   preference, which decides what this file does when the cart has no opinion
   yet — for example on their first add-to-cart of a new session.

   WHY THE LINE IS HIDDEN RATHER THAN SKIPPED
   The cart forms post a POSITIONAL `updates[]` array — one entry per line
   item, in cart order. Omitting the protection row from the markup would
   leave that array one short, and Shopify applies it against the real,
   longer cart, silently zeroing a line. So the row is always rendered and
   hidden with `display: none`; hidden inputs still submit, so the array stays
   aligned. Anything that changes the cart re-renders the section, which
   rebuilds the array to match.

   WHY THIS IS A SEPARATE ASSET FILE
   Dawn's cart drawer re-renders by assigning innerHTML on .drawer__inner, and
   browsers never execute <script> tags that arrive via innerHTML. Anything
   inline in the drawer would run once and die on the first cart update.
   Everything here is delegated off `document` and re-syncs from the DOM, so
   it survives every re-render. (snippets/cart-drawer.liquid documents the same
   trap at length for the upsell.)
   ========================================================================== */

(function () {
  'use strict';

  // The tag is emitted from both snippets/cart-drawer.liquid and
  // sections/main-cart-footer.liquid so the behaviour is present whichever of
  // the two cart surfaces the theme is configured to use. On /cart both are
  // rendered, so guard against binding every listener twice.
  if (window.__alsoGreenShippingProtection) return;
  window.__alsoGreenShippingProtection = true;

  var BLOCK = '[data-shipping-protection]';
  var STORAGE_KEY = 'alsogreen:shipping-protection';
  var RESYNC_DELAY = 200;

  // Resolved lazily from the first block we see, then shared by every block on
  // the page so the drawer and the /cart page can never disagree.
  var optedIn = null;

  // Only one cart write may be in flight at a time. A flip that arrives while
  // one is running sets resyncQueued, and the tail of syncCart runs again --
  // so rapid toggling always settles on the customer's final choice.
  var syncing = false;
  var resyncQueued = false;
  var resyncTimer = null;

  /* ---- helpers ---------------------------------------------------------- */

  function noop() {}

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function firstBlock() {
    return document.querySelector(BLOCK);
  }

  function allBlocks() {
    return Array.prototype.slice.call(document.querySelectorAll(BLOCK));
  }

  function closestFrom(target, selector) {
    if (!target || typeof target.closest !== 'function') return null;
    return target.closest(selector);
  }

  function remembers(block) {
    return block.dataset.remember === 'true';
  }

  function readStored() {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // Private browsing / blocked storage — fall back to the default.
      return null;
    }
  }

  function writeStored(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (e) {
      /* nothing we can do, and nothing that should break the cart */
    }
  }

  /* ---- opt-in state ----------------------------------------------------- */

  function resolveOptIn(block) {
    if (optedIn !== null) return optedIn;

    if (remembers(block)) {
      var stored = readStored();
      if (stored === '1') {
        optedIn = true;
        return optedIn;
      }
      if (stored === '0') {
        optedIn = false;
        return optedIn;
      }
    }

    optedIn = block.dataset.defaultOn === 'true';
    return optedIn;
  }

  function setOptIn(value, block) {
    optedIn = !!value;
    if (remembers(block)) writeStored(optedIn ? '1' : '0');
    sync();
  }

  // An emptied cart is the end of that shopping trip, so the customer's choice
  // retires with it and the next cart starts from the default again.
  //
  // Without this, a single "no thanks" would opt that customer out
  // permanently — for as long as their browser keeps localStorage — which is
  // not what "on by default for all customers" means. Scoping the memory to
  // the cart still honours a deliberate opt-out for as long as it is relevant.
  function forgetChoice() {
    optedIn = null;
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* nothing to clear */
    }
  }

  // Pushes the current state onto every rendered toggle. Also re-applies the
  // hidden-line safety net, for any cart surface whose Liquid was not patched
  // (an app drawer, say) — the real hiding is done server-side.
  function sync() {
    var blocks = allBlocks();
    if (!blocks.length) return;

    blocks.forEach(function (block) {
      var input = block.querySelector('.sp-block__input');
      if (input) input.checked = resolveOptIn(block);
    });

    hideRenderedLines(parseInt(blocks[0].dataset.variantId, 10));
    syncEmptyState();
  }

  // Removing the customer's last real product takes protection out with it,
  // which means two actors write to the cart at once: Dawn's remove request
  // and ours. Dawn toggles cart-drawer.is-empty from the item_count IT saw,
  // and if its response lands after ours that count is already stale — the
  // class comes back off and the drawer renders as a blank white panel.
  //
  // So don't trust any item_count. The server renders .drawer__inner-empty
  // only for an empty cart, so its presence in the markup just applied is the
  // one statement of emptiness that cannot be stale.
  function syncEmptyState() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    var inner = drawer.querySelector('.drawer__inner');
    if (!inner) return;

    drawer.classList.toggle('is-empty', !!inner.querySelector('.drawer__inner-empty'));
  }

  function hideRenderedLines(variantId) {
    if (!variantId) return;
    var nodes = document.querySelectorAll(
      '[data-variant-id="' + variantId + '"], [data-quantity-variant-id="' + variantId + '"]'
    );
    Array.prototype.forEach.call(nodes, function (node) {
      var row = closestFrom(node, 'tr.cart-item, .cart-item');
      if (row) row.classList.add('sp-hidden-line');
    });
  }

  /* ---- cart plumbing ---------------------------------------------------- */

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      if (!response.ok) throw new Error('shipping-protection: ' + response.status);
      return response.json();
    });
  }

  function getCart() {
    return fetch(root() + 'cart.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (response) {
      return response.json();
    });
  }

  function findLine(cart, variantId) {
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].variant_id === variantId) return cart.items[i];
    }
    return null;
  }

  // The cart page's section ids are template-scoped (e.g.
  // "template--123__main-cart-items"), so read them off the DOM rather than
  // guessing. The drawer and the header bubble are plain section files.
  function sectionIds() {
    var ids = ['cart-drawer', 'cart-icon-bubble'];
    ['main-cart-items', 'main-cart-footer'].forEach(function (domId) {
      var el = document.getElementById(domId);
      if (el && el.dataset.id) ids.push(el.dataset.id);
    });
    return ids;
  }

  function sectionPayload() {
    return { sections: sectionIds().join(','), sections_url: window.location.pathname };
  }

  function addProtection(variantId) {
    var payload = sectionPayload();
    payload.items = [
      {
        id: variantId,
        quantity: 1,
        // A leading underscore keeps the property hidden from the cart,
        // checkout, order confirmation and packing slips. It exists so the
        // line can be identified downstream — in order reporting, say —
        // without matching on the product title.
        properties: { _shipping_protection: 'true' }
      }
    ];
    return postJSON(root() + 'cart/add.js', payload);
  }

  function changeLine(key, quantity) {
    var payload = sectionPayload();
    payload.id = key;
    payload.quantity = quantity;
    return postJSON(root() + 'cart/change.js', payload);
  }

  /* ---- section re-rendering --------------------------------------------- */

  function sectionInnerHTML(html, selector) {
    if (!html) return null;
    var found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return found ? found.innerHTML : null;
  }

  function applySections(state) {
    if (!state || !state.sections) return;

    var drawerInner = sectionInnerHTML(state.sections['cart-drawer'], '.drawer__inner');
    var drawerTarget = document.querySelector('cart-drawer .drawer__inner');
    if (drawerInner !== null && drawerTarget) drawerTarget.innerHTML = drawerInner;

    var bubble = sectionInnerHTML(state.sections['cart-icon-bubble'], '.shopify-section');
    var bubbleTarget = document.getElementById('cart-icon-bubble');
    if (bubble !== null && bubbleTarget) bubbleTarget.innerHTML = bubble;

    // Re-rendering .js-contents is what keeps the positional updates[] array
    // in step with the real cart after protection is added or removed.
    ['main-cart-items', 'main-cart-footer'].forEach(function (domId) {
      var el = document.getElementById(domId);
      if (!el || !el.dataset.id) return;
      var contents = sectionInnerHTML(state.sections[el.dataset.id], '.js-contents');
      var target = el.querySelector('.js-contents');
      if (contents !== null && target) target.innerHTML = contents;
    });

    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    sync();
  }

  /* ---- the actual reconciliation ---------------------------------------- */

  // Works out what the cart SHOULD look like, measured on genuine product
  // value only. Protection never counts towards its own display threshold,
  // and a cart holding nothing but protection is not a cart worth protecting.
  //
  // Gift cards are left out too: they are never shipped, so a cart holding
  // only gift cards gets no protection (2026-10-27). productCount still counts
  // them, so the opt-out is only forgotten when the cart is really empty.
  function decide(cart, block) {
    var variantId = parseInt(block.dataset.variantId, 10);
    var minSubtotal = parseInt(block.dataset.minSubtotal, 10) || 0;

    var productItems = cart.items.filter(function (item) {
      return item.variant_id !== variantId;
    });
    var shippableItems = productItems.filter(function (item) {
      return !item.gift_card;
    });
    var productTotal = shippableItems.reduce(function (sum, item) {
      return sum + item.final_line_price;
    }, 0);

    var eligible = shippableItems.length > 0 && productTotal >= minSubtotal;

    return {
      variantId: variantId,
      line: findLine(cart, variantId),
      productCount: productItems.length,
      eligible: eligible,
      want: block.dataset.enabled === 'true' && resolveOptIn(block) && eligible
    };
  }

  // The /cart page's toggle sits outside the .js-contents that Dawn and
  // applySections re-render, so it keeps whatever the page loaded with.
  // Mirror the snippet's own hidden rule here, or removing the last shippable
  // product (leaving only a gift card) would leave the toggle showing.
  function syncVisibility(eligible) {
    allBlocks().forEach(function (block) {
      if (block.dataset.enabled === 'true') block.hidden = !eligible;
    });
  }

  function syncCart() {
    if (syncing) {
      resyncQueued = true;
      return Promise.resolve();
    }

    var block = firstBlock();
    if (!block || !parseInt(block.dataset.variantId, 10)) return Promise.resolve();

    syncing = true;

    return getCart()
      .then(function (cart) {
        var state = decide(cart, block);
        syncVisibility(state.eligible);

        // Note this runs before the removal below, so a cart that empties in
        // the same pass still retires the choice.
        if (state.productCount === 0) forgetChoice();

        if (state.want && !state.line) return addProtection(state.variantId);
        if (state.want && state.line && state.line.quantity !== 1) {
          return changeLine(state.line.key, 1);
        }
        if (!state.want && state.line) return changeLine(state.line.key, 0);
        return null;
      })
      .then(function (result) {
        if (result) applySections(result);
      })
      .catch(function () {
        // The cart is the source of truth. If the write did not land, make the
        // toggle say so rather than promising cover the customer has not got.
        return getCart()
          .then(function (cart) {
            var current = firstBlock();
            if (!current) return;
            optedIn = !!findLine(cart, parseInt(current.dataset.variantId, 10));
            sync();
          })
          .catch(noop);
      })
      .then(function () {
        syncing = false;
        if (resyncQueued) {
          resyncQueued = false;
          return syncCart();
        }
      });
  }

  function scheduleSync() {
    clearTimeout(resyncTimer);
    resyncTimer = setTimeout(syncCart, RESYNC_DELAY);
  }

  /* ---- (?) help popover -------------------------------------------------- */

  // The popover lives inside the cart footer, which the theme pins below the
  // upsell slider in the stacking order. Lift the footer only while a popover
  // is open — see the matching rule in assets/shipping-protection.css.
  function setPopoverLayer(block, open) {
    var footer = closestFrom(block, '.cart-drawer__bottom, .cart__footer, #main-cart-footer');
    if (footer) footer.classList.toggle('sp-popover-open', !!open);
  }

  function closeAllHelp(except) {
    allBlocks().forEach(function (block) {
      var btn = block.querySelector('.sp-block__help-btn');
      var panel = block.querySelector('.sp-block__help');
      if (!btn || !panel || panel === except) return;
      btn.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      setPopoverLayer(block, false);
    });
  }

  function onHelpClick(event) {
    var btn = closestFrom(event.target, '.sp-block__help-btn');
    if (!btn) return;

    event.preventDefault();
    event.stopPropagation();

    var block = closestFrom(btn, BLOCK);
    var panel = block && block.querySelector('.sp-block__help');
    if (!panel) return;

    var open = btn.getAttribute('aria-expanded') === 'true';
    closeAllHelp(panel);
    btn.setAttribute('aria-expanded', open ? 'false' : 'true');
    panel.hidden = open;
    setPopoverLayer(block, !open);
  }

  /* ---- wiring ------------------------------------------------------------ */

  document.addEventListener('click', function (event) {
    if (closestFrom(event.target, '.sp-block__help-btn')) {
      onHelpClick(event);
      return;
    }
    // Any click outside an open panel closes it.
    if (!closestFrom(event.target, '.sp-block__help')) closeAllHelp();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') closeAllHelp();
  });

  // The toggle flips optimistically and the cart catches up. No spinner and
  // nothing disabled: the write is a single AJAX call, and blocking the
  // control would feel slower than the thing it is guarding.
  document.addEventListener('change', function (event) {
    var input = closestFrom(event.target, '.sp-block__input');
    if (!input) return;
    var block = closestFrom(input, BLOCK);
    if (!block) return;

    setOptIn(input.checked, block);
    syncCart();
  });

  // The drawer's markup is replaced wholesale on every cart update, so the
  // freshly rendered <input> starts at its Liquid default and has to be told
  // the current state again. A MutationObserver callback runs as a microtask,
  // i.e. before the next paint, so this never flickers.
  //
  // The debounced syncCart on the same signal is what keeps protection correct
  // when the customer changes the cart itself — removing their last real
  // product takes protection out with it.
  function watch() {
    var targets = [
      document.querySelector('cart-drawer'),
      document.getElementById('main-cart-items'),
      document.getElementById('main-cart-footer')
    ].filter(Boolean);

    targets.forEach(function (target) {
      new MutationObserver(function () {
        sync();
        scheduleSync();
      }).observe(target, { childList: true, subtree: true });
    });
  }

  function init() {
    sync();
    watch();
    syncCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('cart:updated', function () {
    sync();
    scheduleSync();
  });
})();
